import { useQuery } from '@tanstack/react-query';
import { REPORTING_STORE_ORDER_FIELDS } from '@tempo-io/reporting-store-dto';
import * as React from 'react';
import { ItemsApi } from '@/common/api/items';
import type { ItemFieldDto } from '@/common/api/types/items';
import { useFlag } from '@/common/hooks/feature-flags/useFlag';
import { commonQueryKeys } from '@/common/queryKeys/common';
import { withoutPlannerModuleOrderKeys } from '@/features/items/components/table/columns/sortableItemFields';

const EMPTY_SET: ReadonlySet<string> = new Set();

// Hoisted: TanStack reuses select only when reference-equal; an inline arrow churns downstream memos.
const selectSortableFieldNames = (fields: ItemFieldDto[]): ReadonlySet<string> =>
  new Set(fields.filter((f) => f.sortable).map((f) => f.name));

// Sortable fields differ by serving service — offering the wrong one is a 400.
export const useSortableItemFields = (): {
  sortableItemFields: ReadonlySet<string>;
  isLoaded: boolean;
} => {
  const readsReportingStore = useFlag('reportingServiceFilter');
  const plannerModuleEnabled = useFlag('plannerModuleEnabled');
  const { data, isSuccess } = useQuery({
    queryKey: commonQueryKeys.itemFields.byPlannerModule(plannerModuleEnabled),
    queryFn: () => ItemsApi.getItemFields(),
    staleTime: Number.POSITIVE_INFINITY,
    // Nothing reads item-store's answer under the flag.
    enabled: !readsReportingStore,
    select: selectSortableFieldNames,
  });
  const baseSortableItemFields = readsReportingStore
    ? REPORTING_STORE_ORDER_FIELDS
    : (data ?? EMPTY_SET);
  const sortableItemFields = React.useMemo(
    () => withoutPlannerModuleOrderKeys(baseSortableItemFields, plannerModuleEnabled),
    [baseSortableItemFields, plannerModuleEnabled],
  );
  return {
    sortableItemFields,
    // Under the flag the fields are already known, so a failed `/items/fields` shouldn't block the table.
    isLoaded: readsReportingStore || isSuccess,
  };
};
